import { logManager } from '../logs'

// Libraries.
import EventEmitter from 'eventemitter3'

/**
 * Object to represent a "media" object.
 * Wraps a MediaStream object.
 * @class Media
 * TODO: Rename to Stream? Would be more straight-forward since Tracks can be
 *    managed directly (with trackManager), instead of through a "Media" object.
 */
export default function Media (nativeStream, isLocal) {
  const log = logManager.getLogger('Media', nativeStream.id)
  log.info(`Creating new ${isLocal ? 'local' : 'remote'} Media.`)

  // Internal variables.
  const id = nativeStream.id
  const stream = nativeStream
  const emitter = new EventEmitter()

  /**
   * Mapping of the IDs of this Media object's tracks to the Track object.
   * @type {Map}
   */
  const tracks = new Map()

  /**
   * Retrieve a snapshot of the Media object's current state.
   * @method getState
   * @return {Object}
   */
  function getState () {
    return {
      id: id,
      tracks: Array.from(tracks.values()).map(track => track.getState()),
      isLocal: isLocal
    }
  }

  /**
   * Add a track to this Media object.
   * @method addTrack
   * @param {Track} track The Track object to add to the Media object.
   */
  function addTrack (track) {
    if (tracks.has(track.id)) {
      log.debug(`Track (${track.id}) is already in Media (${id}).`)
      return
    }

    // Add the native MediaStreamTrack to the MediaStream.
    stream.addTrack(track.track)
    // Add the Track to the Media object.
    tracks.set(track.id, track)

    /**
     * When a track ends, remove it from the Media object then clean it up.
     */
    track.on('ended', () => {
      const removedTrack = removeTrack(track.id)
      // Might be from old already deleted media which has no tracks anymore.
      if (removedTrack) {
        emitter.emit('track:ended', {
          mediaId: id,
          trackId: removedTrack.id
        })
      }
    })
    // TODO: Emit event or return result?
    emitter.emit('track:new', {
      mediaId: id,
      trackId: track.id
    })

    /**
     * When a track is muted, we generate our own internal event
     */
    track.on('muted', event => {
      const wrappedTrack = getTrack(event.trackId)
      if (wrappedTrack && wrappedTrack.track && wrappedTrack.track.muted) {
        // Emit our internal event.
        // Note that wrappedTrack.id is same as wrappedTrack.track & event.trackId
        emitter.emit('track:muted', {
          mediaId: id,
          trackId: event.trackId
        })
      }
    })

    /**
     * When a track is unmuted, we generate our own internal event
     */
    track.on('unmuted', event => {
      const wrappedTrack = getTrack(event.trackId)
      if (wrappedTrack && wrappedTrack.track && wrappedTrack.track.muted === false) {
        // Emit our internal event.
        // Note that wrappedTrack.id is same as wrappedTrack.track & event.trackId
        emitter.emit('track:unmuted', {
          mediaId: id,
          trackId: event.trackId
        })
      }
    })
  }

  /**
   * Remove a track from the Media object.
   * @private
   * @method removeTrack
   * @param  {string} trackId The Track to remove.
   */
  function removeTrack (trackId) {
    const track = getTrack(trackId)
    if (track) {
      tracks.delete(trackId)
      return track
    }
  }

  /**
   * Stop all Tracks within this Media object.
   * @method stop
   */
  function stop () {
    getTracks().forEach(track => {
      track.stop()
    })
    emitter.emit('media:stopped', this.id)
  }

  /**
   * Retrieve the list of Track object this this Media object manages.
   * @method getTracks
   * @return {Array} The list of Track objects.
   */
  function getTracks () {
    return Array.from(tracks.values())
  }

  /**
   * Retrieve a specific Track object from within the Media object.
   * @method getTrack
   * @param  {String} trackId ID of the Track to retrieve.
   * @return {Track}
   */
  function getTrack (trackId) {
    return tracks.get(trackId)
  }

  function on (...args) {
    return emitter.on(...args)
  }

  function once (...args) {
    return emitter.once(...args)
  }

  function off (...args) {
    return emitter.off(...args)
  }

  /**
   * The exposed API.
   */
  return {
    id,
    tracks,
    isLocal,
    // Getter APIs.
    getState,
    getTracks,
    getTrack,
    // Create APIs.
    addTrack,
    // Cleanup APIs.
    stop,
    // Event APIs.
    on,
    once,
    off
  }
}
